import * as React from "react";
import { useTranslate } from "ra-core";
import {
  DatagridConfigurable,
  useRecordContext,
  ListBase,
  useListContext,
} from "react-admin";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export type BuildColumns = (
  level: number,
  t: (key: string) => string,
) => React.ReactElement[];

interface TreeDatagridProps {
  reference: string;
  target: string; // поле в дочерней сущности, указывающее на родителя (например, "parent_id")
  parentId: string | number | null | undefined;
  showChildrenThead?: boolean; // если true, то будет отображаться заголовок для дочерних элементов
  level?: number;
  buildColumns: BuildColumns;
  sort?: { field: string; order: "ASC" | "DESC" };
  perPage?: number;
  inheritedFilters?: Record<string, any>;
}

export const TreeDatagrid: React.FC<TreeDatagridProps> = ({
  reference,
  target,
  parentId,
  level = 0,
  showChildrenThead = false,
  buildColumns,
  sort = { field: "updated_at", order: "DESC" },
  perPage = 25,
  inheritedFilters,
}) => {
  const t = useTranslate();
  const parentList = useListContext();

  // Фильтры из корневого List (включая введённые пользователем)
  const rootFilters = React.useMemo<Record<string, any>>(() => {
    return inheritedFilters ?? parentList?.filterValues ?? {};
  }, [inheritedFilters, parentList?.filterValues]);

  // Исключаем собственный target, чтобы не конфликтовал с текущим parentId
  const { [target]: _ignored, ...restRoot } = rootFilters || {};
  const combinedFilter = React.useMemo(
    () => ({
      ...restRoot,
      [target]: parentId,
    }),
    [restRoot, target, parentId],
  );

  // На вложенных уровнях скрываем thead и пагинацию
  const nestedHideSx = React.useMemo<SxProps<Theme> | undefined>(() => {
    if (level > 0 && !showChildrenThead) {
      return {
        "& thead": { display: "none" },
        "& .RaDatagrid-headerCell": { display: "none" },
        "& .MuiTablePagination-root": { display: "none" },
      };
    }
    return undefined;
  }, [level, showChildrenThead]);

  return (
    <Box>
      <ListBase
        resource={reference}
        filter={combinedFilter}
        perPage={perPage}
        sort={sort}
        disableSyncWithLocation
      >
        <DatagridConfigurable
          rowClick="expand"
          bulkActionButtons={false}
          expand={
            <ExpandChildren
              showChildrenThead={showChildrenThead}
              reference={reference}
              target={target}
              level={level + 1}
              buildColumns={buildColumns}
              sort={sort}
              perPage={perPage}
              inheritedFilters={rootFilters}
            />
          }
          sx={nestedHideSx}
        >
          {buildColumns(level, t)}
        </DatagridConfigurable>
      </ListBase>
    </Box>
  );
};

interface ExpandChildrenProps {
  reference: string;
  target: string;
  level?: number;
  buildColumns: BuildColumns;
  sort?: { field: string; order: "ASC" | "DESC" };
  perPage?: number;
  inheritedFilters?: Record<string, any>;
  showChildrenThead?: boolean;
}

export const ExpandChildren: React.FC<ExpandChildrenProps> = ({
  reference,
  target,
  level = 1,
  buildColumns,
  sort,
  perPage,
  inheritedFilters,
  showChildrenThead,
}) => {
  const record = useRecordContext();
  if (!record) return null as any;
  return (
    <TreeDatagrid
      reference={reference}
      target={target}
      parentId={record.id}
      showChildrenThead={showChildrenThead}
      level={level}
      buildColumns={buildColumns}
      sort={sort}
      perPage={perPage}
      inheritedFilters={inheritedFilters}
    />
  );
};

export default TreeDatagrid;
